import { useCallback } from 'react';
import * as Cesium from 'cesium';

export function useMeasurement(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  measureDataSourceRef: React.MutableRefObject<any>,
  measureHandlerRef: React.MutableRefObject<Cesium.ScreenSpaceEventHandler | null>,
  measureStepRef: React.MutableRefObject<0 | 1>,
  measurePtARef: React.MutableRefObject<Cesium.Cartographic | null>,
  setMeasureDistanceKm: (distance: number | null) => void,
) {
  const startMeasureMode = useCallback(() => {
    const v = viewerRef.current;
    const mds = measureDataSourceRef.current;
    if (!v || !mds) return;

    // Clean up any existing measurement
    measureHandlerRef.current?.destroy();
    mds.entities.removeAll();
    setMeasureDistanceKm(null);
    measureStepRef.current = 0;
    measurePtARef.current = null;

    const handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas);
    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const ray = v.camera.getPickRay(click.position);
      if (!ray) return;
      const intersection = v.scene.globe.pick(ray, v.scene);
      if (!intersection) return;
      const cart = Cesium.Cartographic.fromCartesian(intersection);
      if (!cart) return;

      if (measureStepRef.current === 0) {
        measurePtARef.current = cart;
        measureStepRef.current = 1;
        mds.entities.removeAll();
        setMeasureDistanceKm(null);
        mds.entities.add({
          id: 'measure_ptA',
          position: intersection,
          point: { pixelSize: 8, color: Cesium.Color.YELLOW, outlineColor: Cesium.Color.WHITE, outlineWidth: 1 },
        });
      } else {
        const ptA = measurePtARef.current!;
        const geo = new Cesium.EllipsoidGeodesic(ptA, cart);
        const distKm = geo.surfaceDistance / 1000;
        setMeasureDistanceKm(distKm);

        mds.entities.add({
          id: 'measure_ptB',
          position: intersection,
          point: { pixelSize: 8, color: Cesium.Color.YELLOW, outlineColor: Cesium.Color.WHITE, outlineWidth: 1 },
        });
        mds.entities.add({
          id: 'measure_line',
          polyline: {
            positions: [
              Cesium.Cartesian3.fromRadians(ptA.longitude, ptA.latitude, 0),
              intersection,
            ],
            width: 1.5,
            material: Cesium.Color.YELLOW.withAlpha(0.8),
            clampToGround: true,
          },
        });
        // Reset for next measurement pair
        measureStepRef.current = 0;
        measurePtARef.current = null;
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    measureHandlerRef.current = handler;
  }, []);

  const stopMeasureMode = useCallback(() => {
    measureHandlerRef.current?.destroy();
    measureHandlerRef.current = null;
    measureDataSourceRef.current?.entities.removeAll();
    setMeasureDistanceKm(null);
    measureStepRef.current = 0;
    measurePtARef.current = null;
  }, []);

  return {
    startMeasureMode,
    stopMeasureMode,
  };
}
